import moment from 'moment';
import Api from 'services/Api';
import * as _ from 'lodash';

const mutationTypes = {
    UPDATE_VALUE: 'UPDATE_VALUE',
    UPDATE_PENSION_VALUE: 'UPDATE_PENSION_VALUE',
    RESET: 'RESET',
};

const isPersonUserInputValid = (state) => {
    return (
        state.gender !== null
        && state.dateOfBirth !== null
        && state.dateOfBirth !== ''
        && state.dateOfAssumedSettlement !== null
        && state.dateOfAssumedSettlement !== ''
    );
};

const isPensionValid = (state) => {
    let pension = state.pension;

    // Guard against user input
    if (!isPensionUserInputValid(state)) {
        return false;
    }

    // Guard against missing calculations.
    if (
        !pension.pensionLossMultiplier ||
        !pension.totalLoss
    ) {
        return false;
    }

    // Guard against lump sum specific calculations
    if (pension.lumpSumLoss
        && (
            pension.lumpSumLossPresentValue === null ||
            pension.longdenFactor === null
        )
    ) {
        return false;
    }

    return true;
};

const isPensionUserInputValid = (state) => {
    let pension = state.pension;

    if (
        !pension.butForRetirementDate ||
        !pension.butForRetirementAge ||
        !pension.pensionMethod ||
        // !pension.retirementDate || // We don't need this unless we are using the Longden method
        !pension.butForPensionAnnual ||
        pension.pensionAnnual === null ||
        pension.pensionAnnual === '' ||
        pension.lumpSumLoss === null ||
        pension.pensionAnnual > pension.butForPensionAnnual
    ) {
        return false;
    }

    // If Lump Sum Pension Loss, there is additional validation:
    //  - There must be a 'But For' Lump Sum
    //  - The Actual Pension Lump Sum must not be null.
    if (pension.lumpSumLoss
        && (
            !pension.butForPensionLumpSum ||
            pension.pensionLumpSum === null ||
            pension.pensionLumpSum === '' ||
            pension.dateLumpSumPaymentReceived === null ||
            pension.dateLumpSumPaymentReceived === '' ||
            pension.pensionLumpSum > pension.butForPensionLumpSum
        )
    ) {
        return false;
    }

    return true;
};

// Initial State
const state = {
    gender: null,
    dateOfBirth: null,
    dateOfAssumedSettlement: null,
    ageAtTrial: null,
    term: null,
    table21: null,
    table23: null,
    table27: null,
    upperAge: null,
    lowerAge: null,
    lowerMultiplier: null,
    upperMultiplier: null,

    pension: {
        // Ill Health Early Retirement / Disability Pension Scheme
        illHealthDisabilityPension: false,

        // Simple Method or Longden Method
        pensionMethod: null,

        //Period of Accelerated Receipt
        acceleratedReceipt: null,

        // Retirement Dates ('But For' and Actual)
        butForRetirementDate: null,
        butForRetirementAge: null,
        retirementDate: null,
        multiplierToRetirement: null,

        // Applies to Lump Sum and Annual Payments
        lifeMultiplier: null,
        discountFactor: null,

        // Is there a lump sum pension payment?
        lumpSumLoss: null,
        butForPensionLumpSum: 0,
        pensionLumpSum: 0,
        longdenFactor: null,
        lumpSumLossValue: null,
        lumpSumLossPresentValue: null,
        dateLumpSumPaymentReceived: null,

        // Annual Pension ('But For' and Actual)
        butForPensionAnnual: 0,
        pensionAnnual: 0,

        // Pension Loss per Annum ('But For' - 'Actual')
        pensionLossAnnual: null,

        // Pension Loss Multiplier
        pensionLossMultiplier: null,

        // Total Loss Annual (Pension Loss Annual * Pension Loss Multiplier)
        totalLossAnnual: null,

        // Total Loss Annual + Lump Sum Loss
        totalLoss: null,
    },
};

// Getters
const getters = {
    value: state => key => state[key],
    pensionValue: state => key => state.pension[key],
    pension: state => state.pension,
    isPensionUserInputValid: state => isPensionUserInputValid(state),
    isPersonUserInputValid: state => isPersonUserInputValid(state)
};

// Actions
const actions = {
    update({ commit, dispatch, state }, { key, value }) {
        commit(mutationTypes.UPDATE_VALUE, { key, value });

        if (state.dateOfBirth && state.dateOfAssumedSettlement) {
            dispatch('updateAgeAtTrial');
        }
    },
    updateAgeAtTrial({ commit, state }){
        let ageAtTrial = moment(state.dateOfAssumedSettlement, 'DD/MM/YYYY').diff(
            moment(state.dateOfBirth, 'DD/MM/YYYY'), 'years', true);

        commit(mutationTypes.UPDATE_VALUE, { key: 'ageAtTrial', value: ageAtTrial });

    },
    updatePensionValue({ commit, dispatch, state }, { key, value }) {
        commit(mutationTypes.UPDATE_PENSION_VALUE, { key, value });

        if (state.pension.butForRetirementDate && state.pension.dateLumpSumPaymentReceived) {
            dispatch('updateAcceleratedReceipt');
        }
    },
    updateAcceleratedReceipt({ commit, state }) {
        let acceleratedReceipt = moment(state.pension.butForRetirementDate, 'DD/MM/YYYY').diff(
            moment(state.pension.dateLumpSumPaymentReceived, 'DD/MM/YYYY'), 'years', true).toFixed(2);

        commit(mutationTypes.UPDATE_PENSION_VALUE, { key: 'acceleratedReceipt' , value: acceleratedReceipt });
    },
    updateRetirement: _.debounce(({commit, state}) => {
        if (state.gender && state.dateOfBirth) {
            let person = {
                gender: state.gender,
                dateOfBirth: state.dateOfBirth
            };
            return Api.calculators.dates.statePensionAge(person).then((data) => {
                let divided = data.statePension.retirementAge / 5;

                commit(mutationTypes.UPDATE_PENSION_VALUE, { key: 'butForRetirementDate', value: data.statePension.retirementDate });
                commit(mutationTypes.UPDATE_PENSION_VALUE, { key: 'butForRetirementAge', value: data.statePension.retirementAge });
                commit(mutationTypes.UPDATE_VALUE, { key: 'lowerAge', value: (5 * Math.floor(divided)) });
                commit(mutationTypes.UPDATE_VALUE, { key: 'upperAge', value: (5 * Math.ceil(divided)) });
            });
        }
    }, 500),
    calculatePensionLoss: _.debounce(({state, commit}) => {
        if (!isPensionUserInputValid(state) && !isPersonUserInputValid(state)) {
            return;
        }

        let claimant = {
            theClaimant: {
                gender: state.gender,
                dateOfBirth: state.dateOfBirth,
            },
            claimInfo: {
                dateOfAssumedSettlement: state.dateOfAssumedSettlement,
                discountRate: '-0.25'
            }
        };

        return Api.calculators.monetary.pensionLoss(claimant, state.pension).then((data) => {
            commit(mutationTypes.UPDATE_PENSION_VALUE, {key: 'discountFactor', value: data.discountFactor});
            commit(mutationTypes.UPDATE_PENSION_VALUE, {key: 'lifeMultiplier', value: data.lifeMultiplier});
            commit(mutationTypes.UPDATE_PENSION_VALUE, {key: 'longdenFactor', value: data.longdenFactor});
            commit(mutationTypes.UPDATE_PENSION_VALUE, {key: 'lumpSumLossPresentValue', value: data.lumpSumLossPresentValue});
            commit(mutationTypes.UPDATE_PENSION_VALUE, {key: 'multiplierToRetirement', value: data.multiplierToRetirement});
            commit(mutationTypes.UPDATE_PENSION_VALUE, {key: 'pensionLossMultiplier', value: data.pensionLossMultiplier.toFixed(2)});
            commit(mutationTypes.UPDATE_PENSION_VALUE, {key: 'totalLoss', value: data.totalLoss});
            commit(mutationTypes.UPDATE_PENSION_VALUE, {key: 'totalLossAnnual', value: data.totalLossAnnual});
            commit(mutationTypes.UPDATE_PENSION_VALUE, {key: 'acceleratedReceipt', value: data.acceleratedReceipt});
        });
    }, 500),
    reset({ commit }) {
        commit(mutationTypes.RESET);
    },
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE_VALUE](state, payload) {
        state[payload.key] = payload.value;
    },
    [mutationTypes.UPDATE_PENSION_VALUE](state, payload) {
        state.pension[payload.key] = payload.value;
    },
    [mutationTypes.RESET](state) {
        state.gender = null;
        state.dateOfBirth = null;
        state.dateOfAssumedSettlement = null;
        state.ageAtTrial = null;
        state.term = null;
        state.table21 = null;
        state.table23 = null;
        state.table27 = null;
        state.upperAge = null;
        state.lowerAge = null;
        state.lowerMultiplier = null;
        state.upperMultiplier = null;
        state.acceleratedReceipt = null;
        state.pension.illHealthDisabilityPension = false;
        state.pension.pensionMethod = null;
        state.pension.dateLumpSumPaymentReceived = null;
        state.pension.butForRetirementDate = null;
        state.pension.butForRetirementAge = null;
        state.pension.retirementDate = null;
        state.pension.multiplierToRetirement = null;
        state.pension.lifeMultiplier = null;
        state.pension.discountFactor = null;
        state.pension.lumpSumLoss = null;
        state.pension.butForPensionLumpSum = 0;
        state.pension.pensionLumpSum = 0;
        state.pension.longdenFactor = null;
        state.pension.lumpSumLossValue = null;
        state.pension.lumpSumLossPresentValue = null;
        state.pension.butForPensionAnnual = 0;
        state.pension.pensionAnnual = 0;
        state.pension.pensionLossAnnual = null;
        state.pension.pensionLossMultiplier = null;
        state.pension.totalLossAnnual = null;
        state.pension.totalLoss = null;
        state.pension.acceleratedReceipt = null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/mini/lossOfPension.js